<template>
  <div id="academic_wbb">
    <usc-hero :title="this.hero.title" :loResSrc="this.hero.loResSrc" :hiResSrc="this.hero.hiResSrc" :color="this.page.color"></usc-hero>
    <usc-intro :copy="this.intro">
      <img class="wbb-logos mb" src="static/WBB.jpg" alt="World Bachelor in Business partnership logos">
    </usc-intro>
    <div class="body gutter-lg mb">
      <p>Much more than a study-abroad program, each university was chosen based on both academic excellence and geography. Studying and working in Los Angeles, Hong Kong and Milan offers students a unique international business growth experience. Los Angeles is a hub for global trade, Hong Kong’s multilateral trading system offers interesting insight into free trade and Milan is seen as the gateway city into the European market.</p>
      <img style="width: 100%;" src="../../../static/academics-WBB-misc.png" alt="Infographic: Top 3 Universities, Continents, Degrees, Languages. 5+ Countries visited" title="Infographic: Top 3 Universities, Continents, Degrees, Languages. 5+ Countries visited" />
    </div>
    <div class="body gutter-lg mb">
      <h2>FIRST YEAR: USC Marshall, Los Angeles, California</h2>
      <p>At Marshall, students will be immersed in business from day one. The first year includes foundational business courses like Microeconomics for Business, along with classes designed to build essential business skills like Writing and Critical Reasoning. In Leading Organizations, which focuses on leadership and management skills, students participate in hands-on projects at the Experiential Learning Center, one of the top business training facilities at any institution of higher education.</p>
      <p>Through business simulations, students put theory into practice and develop leadership, negotiation and communication skills. The WBB cohort travels to an additional international market outside of the three zones included in the degree for exposure to another commercial hub with a range of industries and companies represented. Here, students will receive their first collegiate foray into consulting as they work with an international NGO client, ultimately proposing solutions to a real-world business problem.</p>
    </div>
    <div class="body gutter-lg mb">
      <h2>SECOND YEAR: Hong Kong University of Science and Technology (HKUST), Hong Kong, China</h2>
      <p>Leveraging HKUST’s strengths as a school of science and technology, students will gain professional and technical knowledge while developing a strong entrepreneurial spirit and innovative thinking — the necessary foundation and skills for an evolving world. Courses will offer unique insights into the way business works in China and Asia and provide an introduction to the Chinese languages. Other courses such as Mathematics for Economists, Business Statistics and Introduction to Information Systems emphasize mathematical and analytical skills. Students will take their first accounting class and develop critical-thinking skills studying science and technology and other subjects.</p>
      <p>In both the first and second years, students can select from a variety of humanities courses (literature, music, philosophy, art, anthropology, religion, history, etc.) designed to encourage an appreciation of Western and/or Chinese cultures and to develop analytical and communication skills.</p>
    </div>
    <div class="body gutter-lg mb">
      <h2>THIRD YEAR: Università Commerciale Luigi Bocconi, Milan, Italy</h2>
      <p>The Bocconi curriculum focuses on finance, economics and law. Courses include Corporate Finance, Financial Markets and Institutions, and European Economic Policy. Students examine the fundamentals of Italian civil law within the broader framework of European legal culture in Introduction to the Legal System, and analyze economic developments in Western nations in Economic History. The course in public management will explore the complex relations between private businesses and the government bodies for whom they supply products, services and management know-how at the national and local levels.</p>
      <p>In additional classes, students learn about the specifics of doing business in the European Union and build introductory Italian language skills.</p>
    </div>
    <div class="body gutter-lg mb">
      <h2>FINAL YEAR: Choose What’s Next</h2>
      <p>The fourth and final year reinforces an integrated view of business with courses in business communications, ethics and strategy. WBB students entering their fourth year have the choice of completing their final year in Los Angeles, Hong Kong or Milan, with a focus on the discipline they would like to pursue after graduation. Year four also allows students to emphasize specific interests and objectives, not only in the choice of which school to attend, but also through the selection of elective courses.</p>
    </div>
    <usc-course-chart :dataSet="courseData">
      <img slot="image" src="../../../static/USC_Marshall_CurriculumMap_2023_WBB_R2.png" alt="Course chart for World Bachelor in Business">
    </usc-course-chart>

  </div>
</template>

<script>
import hero from '@/components/Hero'
import intro from '@/components/Intro'
import CourseChart from '@/components/CourseChart/CourseChart'
import Cta from '@/components/Cta'

export default {
  name: 'UndergradBA',
  components: {
    'usc-hero': hero,
    'usc-intro': intro,
    'usc-course-chart': CourseChart,
    'usc-cta': Cta
  },
  data () {
    return {
      page: {
        color: '#ffcc00'
      },
      hero: {
        title: 'World Bachelor in Business',
        loResSrc: '../../../static/HeroImages/Academics4.jpg',
        hiResSrc: '../../../static/HeroImages/Academics4.jpg'
      },
      intro: [
        'Learn from business leaders in at least three countries and earn three degrees on three continents from three prestigious universities through our one-of-a-kind World Bachelor in Business (WBB). WBB is the first-ever four-year, three-continent, three-degree undergraduate business program in the world. Students in the program study at Marshall, Hong Kong University of Science and Technology and Milan’s Universita Commerciale Luigi Bocconi.'
      ],
      courseData: {
        title: 'World Bachelor in Business Program',
        subtitle: '',
        key: [
          {
            courseColor: 'marshall-core',
            courseLabel: 'Marshall Core'
          },
          {
            courseColor: 'usc-requirement',
            courseLabel: 'USC Requirement'
          },
          {
            courseColor: 'marshall-elective',
            courseLabel: 'Marshall Elective'
          }
        ],
        years: {
          freshman: {
            yearTitle: 'FIRST YEAR: USC',
            semesters: {
              Fall: [
                {
                  courseID: 'MATH 118',
                  courseName: 'Fundamental Principles of Calculus',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'WRIT 150',
                  courseName: 'Writing and Critical Reasoning',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'GE-D',
                  courseName: 'Life Sciences',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'BUAD 307',
                  courseName: 'Marketing Fundamentals',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'BUAD 101',
                  courseName: 'Freshman Leadership Seminar',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 2,
                  courseCredits: 2
                }
              ],
              Spring: [
                {
                  courseID: 'ECON 351x',
                  courseName: 'Microeconomics for Business',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'WRIT 340',
                  courseName: 'Advanced Writing for Business',
                  courseColor: 'usc-requirement',
                  hasMin: true,
                  min: 3,
                  courseCredits: 4
                },
                {
                  courseID: 'GE-E',
                  courseName: 'Physical Science w/Lab',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'GESM',
                  courseName: 'General Education Seminar',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'Global Social Impact',
                  courseName: '',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 2,
                  courseCredits: 2
                }
              ]
            }
          },
          sophmore: {
            yearTitle: 'SECOND YEAR: HKUST',
            semesters: {
              Fall: [
                {
                  courseID: 'ECON 352x',
                  courseName: 'Macroeconomics for Business',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 4,
                  courseCredits: 4
                },
                {
                  courseID: 'MGMT 2030',
                  courseName: 'Business Communication',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 3,
                  courseCredits: 3
                },
                {
                  courseID: 'CC3/GE-A',
                  courseName: 'The Arts',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 3,
                  courseCredits: 3
                },
                {
                  courseID: 'MGMT 2110',
                  courseName: 'Organizational Behavior',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 3,
                  courseCredits: 3
                },
                {
                  courseID: 'ACCT 2010',
                  courseName: 'Principles of Accounting I',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 3,
                  courseCredits: 3
                }
              ],
              Spring: [
                {
                  courseID: 'WBBA 2010 & 2020',
                  courseName: 'Understanding Business in Asia',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 3,
                  courseCredits: 3
                },
                {
                  courseID: 'ISOM 2010 GE-F',
                  courseName: 'Intro to Information Systems',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 3,
                  courseCredits: 3
                },
                {
                  courseID: 'ISOM 2500 GE-F',
                  courseName: 'Business Statistics',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 3,
                  courseCredits: 3
                },
                {
                  courseID: 'ISOM 2700',
                  courseName: 'Chinese Communication',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 3,
                  courseCredits: 3
                },
                {
                  courseID: 'CC2/GE-B',
                  courseName: 'Humanistic Inquiry',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 3,
                  courseCredits: 3
                }
              ]
            }
          },
          junior: {
            yearTitle: 'THIRD YEAR: BOCCONI',
            semesters: {
              Fall: [
                {
                  courseID: '30367',
                  courseName: 'Finance I (Financial Markets)',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 3,
                  courseCredits: 3
                },
                {
                  courseID: '30047',
                  courseName: 'Introduction to the Legal System',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 3,
                  courseCredits: 3
                },
                {
                  courseID: '30364',
                  courseName: 'Accounting (Managerial Acct)',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 3,
                  courseCredits: 3
                },
                {
                  courseID: '30372 GE-C/H',
                  courseName: 'Global History',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 3,
                  courseCredits: 3
                },
                {
                  courseID: 'Financial Mathematics',
                  courseName: '',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 3,
                  courseCredits: 3
                }
              ],
              Spring: [
                {
                  courseID: '30366',
                  courseName: 'Finance II (Corporate Finance)',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 3,
                  courseCredits: 3
                },
                {
                  courseID: '30048',
                  courseName: 'Introduction to the Legal System II',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 3,
                  courseCredits: 3
                },
                {
                  courseID: '30056 GE-C/G',
                  courseName: 'European Economic Policy',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 3,
                  courseCredits: 3
                },
                {
                  courseID: '30152',
                  courseName: 'Public Management',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 3,
                  courseCredits: 3
                },
                {
                  courseID: 'European Language',
                  courseName: '',
                  courseColor: 'usc-requirement',
                  hasMin: false,
                  min: 3,
                  courseCredits: 3
                },
                {
                  courseID: '30361',
                  courseName: 'European Business',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: 1,
                  courseCredits: 1
                }
              ]
            }
          },
          senior: {
            yearTitle: 'FINAL YEAR',
            semesters: {
              Fall: [
                {
                  courseID: 'Operations Management',
                  courseName: '',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: '',
                  courseCredits: ''
                },
                {
                  courseID: 'Business Strategy',
                  courseName: '',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: '',
                  courseCredits: ''
                },
                {
                  courseID: 'Social and Ethical Issues',
                  courseName: '',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: '',
                  courseCredits: ''
                },
                {
                  courseID: 'Marshall Upper Division Elective/Emphasis',
                  courseName: '',
                  courseColor: 'marshall-elective',
                  hasMin: false,
                  min: '',
                  courseCredits: ''
                }
              ],
              Spring: [
                {
                  courseID: 'Final Report, Analysis, Research',
                  courseName: '',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: '',
                  courseCredits: ''
                },
                {
                  courseID: 'Business Law',
                  courseName: '',
                  courseColor: 'marshall-core',
                  hasMin: false,
                  min: '',
                  courseCredits: ''
                },
                {
                  courseID: 'Marshall Upper Division Elective/Emphasis',
                  courseName: '',
                  courseColor: 'marshall-elective',
                  hasMin: false,
                  min: '',
                  courseCredits: ''
                },
                {
                  courseID: 'Marshall Upper Division Elective/Emphasis',
                  courseName: '',
                  courseColor: 'marshall-elective',
                  hasMin: false,
                  min: '',
                  courseCredits: ''
                }
              ]
            }
          }
        }
      }
    }
  },
  created () {
    this.$root.setPageHeader('Academics', 'World Bachelor in Business')
  }
}
</script>

<style scoped lang="scss">
  .wbb-logos {
    width: 100%;
    max-width: 870px;
    margin: 0 auto;
    display: block;
  }
</style>



// WEBPACK FOOTER //
// src/pages/academics/Wbb.vue